import moment, { MomentInput } from "moment";
import Gantt from "PFApp/components/gantt/gantt";
import { ProfileAvatarConnected } from "PFApp/components/profile_avatar_connected";
import { useRoleJobCode } from "PFApp/use_role_job_code";
import Tooltip from "PFComponents/tooltip/tooltip";
import { getProfileName } from "PFCore/helpers/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { MatchedProfile, Meta, Profile } from "PFTypes";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./planner_gantt.less";

type RowItemProps = {
  profileData: MatchedProfile;
};

const RowItem = ({ profileData }: RowItemProps) => {
  const { t } = useTranslation("workflow", { keyPrefix: "parts.planner" });
  const { profile, optionalFields } = profileData;

  const hoursAvailable =
    !isNaN(parseInt(String(optionalFields?.availableMinutes))) &&
    Math.round(parseInt(String(optionalFields?.availableMinutes)) / 60);

  const username = getProfileName(profile as Profile);

  return (
    <div className={css.label}>
      <div>
        <ProfileAvatarConnected profile={profile as Profile} size={40} smartLink />
      </div>
      <div className={css.details}>
        <Tooltip content={<div className={css.tooltip}>{username}</div>}>
          <div className={css.name}>{username}</div>
        </Tooltip>
        {(hoursAvailable || hoursAvailable === 0) && (
          <div>{t("countHoursAvailable", { count: hoursAvailable })}</div>
        )}
      </div>
    </div>
  );
};

type InfoPanelProps = {
  data?: {
    entries: MatchedProfile[];
    meta: {
      totalAvailableMinutes: number;
      total: number;
    };
  };
};

const InfoPanel = ({ data }: InfoPanelProps) => {
  const { t } = useTranslation("workflow", { keyPrefix: "parts.planner" });

  const totalHoursAvailable = useMemo(() => {
    if (!data || isNaN(data.meta.totalAvailableMinutes)) {
      return false;
    }
    /* sum items to avoid issue when rounded total is different than sum of rounded items */
    if (data.entries.length === data.meta.total) {
      return data.entries.reduce(
        (sum, item) =>
          sum +
          (isNaN(item.optionalFields?.availableMinutes || 0)
            ? 0
            : Math.round(parseInt(String(item.optionalFields?.availableMinutes || 0)) / 60)),
        0
      );
    } else {
      return Math.round(parseInt(String(data.meta?.totalAvailableMinutes || 0)) / 60);
    }
  }, [data]);

  return (
    <div className={css.infoPanel}>
      {(totalHoursAvailable || totalHoursAvailable === 0) && (
        <div>{t("countTotalHoursAvailable", { count: totalHoursAvailable })}</div>
      )}
    </div>
  );
};

type PlannerGanttProps = {
  data?: {
    entries: MatchedProfile[];
    meta: Meta & {
      totalAvailableMinutes: number;
      total: number;
    };
  };
  monthsLimit: number;
  matchMonthsLimit: number;
};

export const PlannerGantt = ({ data, monthsLimit, matchMonthsLimit }: PlannerGanttProps) => {
  const jobCodeDisplayAs = useRoleJobCode();
  const { formatISODate } = useDateFormatter();

  const availabilityStart = data?.meta.filters?.availability?.start;

  const startDate = useMemo(() => {
    const date = moment
      .utc(availabilityStart as MomentInput)
      .startOf("isoWeek")
      .startOf("day");

    date.startOf("isoWeek").subtract(7, "days");
    return formatISODate(date);
  }, [availabilityStart, formatISODate]);

  return (
    <Gantt
      style={{ margin: "0 0 30px 0", borderRadius: 10 }}
      monthsLimit={monthsLimit}
      matchMonthsLimit={matchMonthsLimit}
      rowItems={data?.entries || []}
      startDate={startDate}
      availabilityInfo={{}}
      infoPanel={<InfoPanel data={data} />}
      rowItemLabelComponent={(profileData) => <RowItem profileData={profileData} />}
      showToday
      showClashes
      jobCodeDisplayAs={jobCodeDisplayAs}
      utmSource="workflow"
    />
  );
};

PlannerGantt.displayName = "talent";
